// Rolling several sources' health up into the one status a category shows.
//
// WHY THIS EXISTS. A panel toggle is a category; a category can hold more than
// one source in the same region. Scotland's Soil type is the case that matters:
// `hutton-soil-250k-scotland` works, and `hutton-soil-25k-scotland` is still on
// druid and druid is still down. Both wrote their status to the same category
// and the last writer won, so the dead sibling libelled a layer the farmer could
// plainly see on screen. Davin, 19/08/2026, on the working Scottish soil layer:
// *"it says slow, then says failed, even tho the layer loads super quick"*.
//
// THE RULE: the status describes what the farmer can see, so anything drawn wins.
// One source loading successfully means there is data on the map, and no number
// of dead siblings changes that. A category only reports a problem when nothing
// in it has drawn.
//
// WHAT THIS DELIBERATELY GIVES UP. Soil 1:25k can no longer report itself
// unavailable, because it shares its toggle with the 250k layer that works. The
// definition of done in clock-in 001 asked for 1:25k to "fail honestly rather
// than silently", and under one shared toggle that is not achievable without
// either splitting the toggle or naming a source to a farmer, and
// "Hutton Soils 25K is unavailable" means nothing to them. Silence is the honest
// answer here: soil data IS on screen. The missing 1:25k detail is a
// data-completeness matter, not a farmer-facing failure.

// Best to worst. Progress outranks worry, and drawn outranks both: a category
// where one source has only just started asking is not yet "slow", even if an
// older sibling has given up.
const PRECEDENCE = ['loaded', 'partial', 'loading', 'slow', 'unavailable'];

/**
 * @param {Array<string|undefined>} statuses every status reported by one
 *   category's sources, in any order
 * @returns {string} the status that category should show
 */
export function aggregateSourceStatus(statuses) {
  const present = new Set(statuses.filter(Boolean));
  return PRECEDENCE.find((status) => present.has(status)) ?? 'idle';
}

/**
 * Per-category status, derived from per-source reports.
 *
 * @param {Array<{id: string, sources: Array<{id: string}>}>} catalogue
 * @param {Record<string, {status?: string}>} sourceStatuses  sourceId -> report
 * @returns {Record<string, string>} categoryId -> status
 */
export function statusByCategory(catalogue, sourceStatuses) {
  const byCategory = {};
  for (const category of catalogue) {
    byCategory[category.id] = aggregateSourceStatus(
      category.sources.map((source) => sourceStatuses[source.id]?.status),
    );
  }
  return byCategory;
}

/**
 * The first error message worth showing for a category, or undefined.
 *
 * Only consulted when the category is actually reporting a problem, so a stale
 * message from a source that later recovered cannot leak onto a healthy layer.
 */
export function errorMessageForCategory(category, sourceStatuses, aggregated) {
  if (aggregated !== 'unavailable') return undefined;
  for (const source of category.sources) {
    const message = sourceStatuses[source.id]?.errorMessage;
    if (message) return message;
  }
  return undefined;
}
